import React, { useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { EmployeesTable, useTranslator } from "@sia/style-guide";
import { DataCollectionContext } from "contexts/DataCollectionContext";
import { prepareEmployeesForTable } from "../SectionContent/EmployeesSection/utils";
import { prepareHistoricalEmployeesForTable, prepareLegacyOverheads } from "./legacySurveySnapshots";
import PreviousSurveyAccordionFIBU from "./PreviousSurveyAccordionFIBU";
import "./PreviousSurveyData.scss";

const title = ({ surveyYear, surveyId }) => `TEST MODE — Previous survey ${surveyYear || `(ID ${surveyId})`}`;

const snapshotType = PropTypes.shape({
  surveyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  surveyYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  companyInputId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  employees: PropTypes.array,
  companyOverheads: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
});

const FrozenEmployees = ({ snapshot, dropdownOptions }) => {
  const translate = useTranslator();
  const employees = useMemo(
    () => prepareEmployeesForTable(prepareHistoricalEmployeesForTable(snapshot.employees || []), dropdownOptions),
    [dropdownOptions, snapshot.employees]
  );

  if (!employees.length) return null;

  return (
    <div className="sia-padding-06-top sia-padding-03-bottom">
      <h4 className="sia-text-04 sia-padding-02-bottom">{title(snapshot)}</h4>

      <EmployeesTable items={employees} actions={[]} translate={translate} isDisabled />
    </div>
  );
};

FrozenEmployees.propTypes = {
  snapshot: snapshotType.isRequired,
  dropdownOptions: PropTypes.object,
};

FrozenEmployees.defaultProps = {
  dropdownOptions: {},
};

const FrozenOverheads = ({ snapshot }) => {
  const translate = useTranslator();
  const overheads = useMemo(() => prepareLegacyOverheads(snapshot.companyOverheads || [], translate), [snapshot.companyOverheads, translate]);

  if (!overheads.length) return null;

  return (
    <div className="sia-padding-06-top sia-padding-03-bottom">
      <h4 className="sia-text-04 sia-padding-02-bottom">{title(snapshot)}</h4>

      <PreviousSurveyAccordionFIBU
        items={overheads}
        grandTotalText={translate("benchmarking.survey.company-overhead.grand-total")}
      />
    </div>
  );
};

FrozenOverheads.propTypes = {
  snapshot: snapshotType.isRequired,
};

export const PreviousSurveyEmployees = () => {
  const { previousSurveys, dropdownOptionsEmployees } = useContext(DataCollectionContext);

  if (!previousSurveys?.length) return null;

  return (
    <div className="survey-history-test-mode">
      {previousSurveys.map((snapshot) => (
        <FrozenEmployees
          key={`employees-${snapshot.companyInputId || snapshot.surveyId}`}
          snapshot={snapshot}
          dropdownOptions={dropdownOptionsEmployees}
        />
      ))}
    </div>
  );
};

export const PreviousSurveyOverheads = () => {
  const { previousSurveys } = useContext(DataCollectionContext);

  if (!previousSurveys?.length) return null;

  return (
    <div className="survey-history-test-mode">
      {previousSurveys.map((snapshot) => (
        <FrozenOverheads key={`overheads-${snapshot.companyInputId || snapshot.surveyId}`} snapshot={snapshot} />
      ))}
    </div>
  );
};
